import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import { Photo, AlbumOverlayConfig, OVERLAY_SIZE_FACTORS } from '../../types';
import { computeOverlayPlacement } from '../../lib/overlayPlacement';
import { overlaysApi } from '../../api/overlays';
import { Skeleton } from '../UI/Skeleton';
import canvasStyles from './AlbumPhotoCanvas.module.css';

export interface AlbumPhotoCanvasHandle {
  downloadCanvas: () => void;
}

interface OverlayImageProps {
  config: AlbumOverlayConfig;
  canvasWidth: number;
  canvasHeight: number;
  offsetX?: number;
  offsetY?: number;
}

const OverlayImage: React.FC<OverlayImageProps> = ({ config, canvasWidth, canvasHeight, offsetX = 0, offsetY = 0 }) => {
  const [image] = useImage(overlaysApi.fileUrl(config.overlayId), 'anonymous');
  if (!image) return null;

  const sizeFactor = OVERLAY_SIZE_FACTORS[config.size] ?? 0.18;
  const { x, y, scale } = computeOverlayPlacement(
    config.anchor,
    sizeFactor,
    canvasWidth,
    canvasHeight,
    image.width,
    image.height,
  );

  return (
    <KonvaImage
      image={image}
      x={x + offsetX}
      y={y + offsetY}
      scaleX={scale}
      scaleY={scale}
      opacity={config.opacity}
      listening={false}
    />
  );
};

interface AlbumPhotoCanvasProps {
  photo: Photo;
  overlayConfigs: AlbumOverlayConfig[];
  width: number;
  height: number;
  /** Use originalUrl instead of thumbnailUrl (for viewer/download) */
  useOriginal?: boolean;
  /**
   * 'cover' (default) — crops the image to fill the canvas (square grid cells, album cover).
   * 'contain' — fits the entire image within the canvas, no cropping (full-size viewer, edit previews).
   *             Overlays are placed against the actual drawn image rect so positions match the
   *             lambda-composited output.
   */
  fit?: 'cover' | 'contain';
  className?: string;
}

export const AlbumPhotoCanvas = forwardRef<AlbumPhotoCanvasHandle, AlbumPhotoCanvasProps>(
  ({ photo, overlayConfigs, width, height, useOriginal = false, fit = 'cover', className }, ref) => {
    const stageRef = useRef<Konva.Stage>(null);
    const imageUrl = useOriginal ? (photo.originalUrl ?? '') : (photo.thumbnailUrl ?? '');
    const [image, status] = useImage(imageUrl, 'anonymous');

    useImperativeHandle(ref, () => ({
      downloadCanvas: () => {
        if (!stageRef.current) return;
        const dataUrl = stageRef.current.toDataURL({ pixelRatio: 2, mimeType: 'image/jpeg', quality: 0.9 });
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = photo.originalFilename.replace(/\.[^.]+$/, '') + '.jpg';
        a.click();
      },
    }));

    const cssVars = { '--canvas-width': `${width}px`, '--canvas-height': `${height}px` } as React.CSSProperties;

    if (status === 'loading' || !imageUrl) {
      return <Skeleton className={`${className ?? ''} ${canvasStyles['skeleton']}`} style={cssVars} />;
    }

    // If the Konva image failed to load (CORS / network), fall back to a
    // regular <img> so the photo is still visible. Overlays won't render in
    // this mode but the user can still see and open the photo.
    if (status === 'failed' || !image) {
      return (
        <div
          className={`${className ?? ''} ${canvasStyles['canvas-wrapper']}`}
          onContextMenu={(e) => e.preventDefault()}
          draggable={false}
          style={cssVars}
          role="img"
          aria-label={photo.originalFilename}
        >
          <img
            src={imageUrl}
            alt={photo.originalFilename}
            draggable={false}
            className={canvasStyles['fallback-img']}
          />
        </div>
      );
    }

    const canvasRatio = width / height;
    const imgRatio = image.width / image.height;

    // Layout: how the image is drawn inside the (width × height) canvas, and the
    // rect against which overlays are positioned.
    let drawX = 0;
    let drawY = 0;
    let drawW = width;
    let drawH = height;
    // Cover-crop fields (only used in 'cover' mode)
    let cropX = 0;
    let cropY = 0;
    let cropW: number | undefined;
    let cropH: number | undefined;

    if (fit === 'contain') {
      // Fit the entire image within the canvas, preserving aspect — no cropping.
      // Overlays are positioned against the drawn image rect (drawW × drawH) so
      // their placement matches the lambda-composited downloadable output.
      if (imgRatio > canvasRatio) {
        drawW = width;
        drawH = width / imgRatio;
        drawX = 0;
        drawY = (height - drawH) / 2;
      } else {
        drawH = height;
        drawW = height * imgRatio;
        drawX = (width - drawW) / 2;
        drawY = 0;
      }
    } else {
      // 'cover': crop the image to fill the canvas without letterbox bars
      // (used by square grid cells and album cover thumbnails).
      if (imgRatio > canvasRatio) {
        cropH = image.height;
        cropW = image.height * canvasRatio;
        cropX = (image.width - cropW) / 2;
        cropY = 0;
      } else {
        cropW = image.width;
        cropH = image.width / canvasRatio;
        cropX = 0;
        cropY = (image.height - cropH) / 2;
      }
    }

    return (
      <div
        className={`${className ?? ''} ${canvasStyles['canvas-wrapper']}`}
        onContextMenu={(e) => e.preventDefault()}
        draggable={false}
        style={cssVars}
        role="img"
        aria-label={photo.originalFilename}
      >
        <Stage ref={stageRef} width={width} height={height}>
          <Layer>
            <KonvaImage
              image={image}
              x={drawX}
              y={drawY}
              width={drawW}
              height={drawH}
              cropX={fit === 'cover' ? cropX : undefined}
              cropY={fit === 'cover' ? cropY : undefined}
              cropWidth={fit === 'cover' ? cropW : undefined}
              cropHeight={fit === 'cover' ? cropH : undefined}
              listening={false}
            />
            {overlayConfigs.map((cfg) => (
              <OverlayImage
                key={cfg.overlayId}
                config={cfg}
                canvasWidth={drawW}
                canvasHeight={drawH}
                offsetX={drawX}
                offsetY={drawY}
              />
            ))}
          </Layer>
        </Stage>
      </div>
    );
  },
);

AlbumPhotoCanvas.displayName = 'AlbumPhotoCanvas';
